import L from "leaflet";

const DEFAULTS = {
  circleSpiralSwitchover: 9,
  circleFootSeparation: 28,
  circleStartAngle: Math.PI / 6,
  spiralLengthStart: 15,
  spiralLengthFactor: 5,
  spiralFootSeparation: 26,
  legWeight: 1.5,
  legColor: "rgba(255, 255, 255, 0.6)",
  legDashArray: "4 4",
  animationDuration: 200
};

export class Spiderfier {
  constructor(map, options = {}) {
    this._map = map;
    this._options = { ...DEFAULTS, ...options };
    this._spiderfied = null;
    this._legs = [];
    this._onUnspiderfy = options.onUnspiderfy || null;

    this._map.on("zoomstart", () => this.unspiderfy());
  }

  isSpiderfied() {
    return !!this._spiderfied;
  }

  findNearbyMarkers(target, allMarkers) {
    const tll = target.getLatLng();
    const nearby = [];

    for (const m of allMarkers) {
      const ll = m.getLatLng();
      if (ll.lat === tll.lat && ll.lng === tll.lng) {
        nearby.push(m);
      }
    }

    return nearby.length > 1 ? nearby : null;
  }

  spiderfy(markers) {
    if (this._spiderfied) {
      this.unspiderfy();
    }

    const centerLatLng = this._computeCentroid(markers);
    const centerPt = this._map.latLngToLayerPoint(centerLatLng);
    const count = markers.length;

    const positions =
      count <= this._options.circleSpiralSwitchover
        ? this._generateCirclePositions(centerPt, count)
        : this._generateSpiralPositions(centerPt, count);

    markers.forEach((marker, i) => {
      const originalLatLng = marker.getLatLng();
      const targetLatLng = this._map.layerPointToLatLng(positions[i]);

      marker._omsData = { originalLatLng };

      const leg = L.polyline([centerLatLng, targetLatLng], {
        weight: this._options.legWeight,
        color: this._options.legColor,
        dashArray: this._options.legDashArray,
        interactive: false
      });
      leg.addTo(this._map);
      this._legs.push(leg);

      this._animateMarker(marker, originalLatLng, targetLatLng, this._options.animationDuration);
      marker.bringToFront();
    });

    this._spiderfied = markers;
  }

  unspiderfy() {
    if (!this._spiderfied) return;

    const markers = this._spiderfied;
    const duration = this._options.animationDuration;
    this._spiderfied = null;

    markers.forEach(marker => {
      if (!marker._omsData) return;
      const currentLatLng = marker.getLatLng();
      this._animateMarker(marker, currentLatLng, marker._omsData.originalLatLng, duration);
    });

    setTimeout(() => {
      this._legs.forEach(leg => this._map.removeLayer(leg));
      this._legs = [];

      markers.forEach(marker => {
        if (marker._omsData) {
          marker.setLatLng(marker._omsData.originalLatLng);
          delete marker._omsData;
        }
      });

      if (this._onUnspiderfy) {
        this._onUnspiderfy();
      }
    }, duration);
  }

  _computeCentroid(markers) {
    let lat = 0;
    let lng = 0;
    for (const m of markers) {
      const ll = m.getLatLng();
      lat += ll.lat;
      lng += ll.lng;
    }
    return L.latLng(lat / markers.length, lng / markers.length);
  }

  _generateCirclePositions(centerPt, count) {
    const circumference = this._options.circleFootSeparation * (2 + count);
    const legLength = Math.max(circumference / (2 * Math.PI), 35);
    const angleStep = (2 * Math.PI) / count;
    const positions = [];

    for (let i = 0; i < count; i++) {
      const angle = this._options.circleStartAngle + i * angleStep;
      positions.push(
        L.point(centerPt.x + legLength * Math.cos(angle), centerPt.y + legLength * Math.sin(angle))
      );
    }
    return positions;
  }

  _generateSpiralPositions(centerPt, count) {
    let legLength = this._options.spiralLengthStart;
    let angle = 0;
    const positions = [];

    for (let i = 0; i < count; i++) {
      angle += this._options.spiralFootSeparation / legLength + i * 0.0005;
      positions.push(
        L.point(centerPt.x + legLength * Math.cos(angle), centerPt.y + legLength * Math.sin(angle))
      );
      legLength += (2 * Math.PI * this._options.spiralLengthFactor) / angle;
    }
    return positions;
  }

  _animateMarker(marker, fromLatLng, toLatLng, duration) {
    const start = performance.now();
    const animate = now => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = t * (2 - t);
      const lat = fromLatLng.lat + (toLatLng.lat - fromLatLng.lat) * eased;
      const lng = fromLatLng.lng + (toLatLng.lng - fromLatLng.lng) * eased;
      marker.setLatLng([lat, lng]);
      if (t < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }

  destroy() {
    this.unspiderfy();
    this._map.off("zoomstart");
  }
}
